// src/pages/UkEssentials.js
// MODIFIED with advanced SEO enhancements

import React, { useState, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { AppContext } from './App'; 
import firebaseConfig from './firebaseConfig'; 

// =================================================================
// == Utility Functions                                         ==
// =================================================================

/**
 * ✨ NEW: Creates a URL-friendly slug from a string.
 * This is used to generate IDs for category headings for anchor links.
 * @param {string} text - The text to slugify.
 * @returns {string} The slugified text.
 */
const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')       // Replace spaces with -
    .replace(/[^\w\-]+/g, '')   // Remove all non-word chars
    .replace(/\-\-+/g, '-');      // Replace multiple - with single -
};

// =================================================================
// == FAQ Component (Accordion Style)                             ==
// =================================================================
const faqData = [
    {
        question: "Can I use my Indian electronics in the UK?",
        answer: "Mostly, yes, but you will always need a Type G plug adapter. The UK runs on 230V/50Hz, which is very similar to India's 220V/50Hz, so most modern electronics like chargers for phones and laptops will work fine with just an adapter. However, for high-power appliances like a mixer-grinder or hair dryer, it's safer and better to buy a UK-specific version to avoid overheating or damage."
    },
    {
        question: "Is tap water safe to drink in the UK?",
        answer: "Yes, the tap water across the UK is among the safest in the world and is perfectly fine to drink. However, it is often 'hard' water, meaning it has a high mineral content. This can affect the taste and cause limescale buildup in kettles. Many people use a water filter jug, like a Brita, to soften the water."
    },
    {
        question: "Do I need a TV Licence in the UK?",
        answer: "You need a TV Licence if you watch or record live TV on any channel (e.g., via Sky, Virgin, Freeview) or if you watch or stream content on BBC iPlayer. You do not need one just to own a TV or to watch streaming services like Netflix, Amazon Prime Video, or YouTube."
    },
    {
        question: "Where can I find authentic Indian spices and groceries in the UK?",
        answer: "Most large supermarkets (like Tesco, Sainsbury's, Asda) have a 'World Foods' aisle with a good selection of basic spices, lentils, and pastes. For a wider, more authentic range, you'll want to find a local South Asian or Indian grocery store. In larger cities like London, Birmingham, and Manchester, areas like Southall, Wembley, or the 'Curry Mile' have many dedicated stores."
    }
];

function FaqItem({ faq, index, isOpen, onToggle }) {
    return (
        <div className="border-b border-gray-200 dark:border-gray-700 py-4">
            {/* ✨ MODIFIED: The question is now an <h3> for better semantic structure. */}
            <button
                onClick={() => onToggle(index)}
                className="w-full flex justify-between items-center text-left text-gray-800 dark:text-gray-200"
            >
                <h3 className="text-lg font-semibold text-left">{faq.question}</h3>
                <svg
                    className={`w-5 h-5 transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
            </button>
            <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 mt-4' : 'max-h-0'}`}
            >
                <p className="text-gray-600 dark:text-gray-300 text-base">
                    {faq.answer}
                </p>
            </div>
        </div>
    );
}

function FaqSection() {
    const [openIndex, setOpenIndex] = useState(null);

    const handleToggle = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6 pb-2 border-b-2 border-indigo-200 dark:border-indigo-800">
                Frequently Asked Questions
            </h2>
            <div className="space-y-2">
                {faqData.map((faq, index) => (
                    <FaqItem key={index} index={index} faq={faq} isOpen={openIndex === index} onToggle={handleToggle} />
                ))}
            </div>
        </div>
    );
}

// =================================================================
// == Main Page Components                                        ==
// =================================================================

function ItemCard({ item }) {
    return (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg shadow-md overflow-hidden flex flex-col">
            <div className="w-full h-48 bg-white flex items-center justify-center p-2">
                 <img 
                     src={item.imageUrl} 
                     alt={item.name}
                     className="max-h-full max-w-full object-contain"
                     // ✨ NEW: Lazy loading improves page speed by deferring off-screen image loads.
                     loading="lazy"
                 />
            </div>
            <div className="p-5 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-indigo-700 dark:text-indigo-400 mb-2">{item.name}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 flex-grow">{item.description}</p>
                <a
                    href={item.amazonLink}
                    target="_blank"
                    // ✨ MODIFIED: Added "sponsored" to the rel attribute for affiliate link best practices.
                    rel="sponsored noopener noreferrer"
                    className="mt-auto inline-block w-full text-center px-4 py-2 rounded-md bg-yellow-500 hover:bg-yellow-600 text-black font-semibold transition duration-200"
                >
                    Check Price on Amazon.co.uk
                </a>
            </div>
        </div>
    );
}

function UkEssentials() {
    const { db } = useContext(AppContext);
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!db) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const collectionPath = `artifacts/${firebaseConfig.appId}/public/data/`;
                
                const categoriesQuery = query(collection(db, `${collectionPath}essentials_categories`), orderBy("priority", "asc"));
                const categorySnapshot = await getDocs(categoriesQuery);
                const fetchedCategories = categorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCategories(fetchedCategories);

                const itemsSnapshot = await getDocs(collection(db, `${collectionPath}essentials_items`));
                const fetchedItems = itemsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setItems(fetchedItems);

            } catch (error) {
                console.error("Error fetching essentials data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [db]);
    
    // ✨ MODIFIED: Consolidated all schemas into a single @graph for best practice.
    // This now includes WebPage, FAQPage, and an ItemList of Products.
    const generatePageSchema = () => {
        const canonicalUrl = "https://indianflatmates.co.uk/uk-essentials-for-indians";
        
        const faqSchema = {
            "@type": "FAQPage",
            "mainEntity": faqData.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        };
        
        const productSchema = {
            "@type": "ItemList",
            "name": "UK Essentials Checklist for Indians",
            "description": "A curated list of essential items for Indians moving to the UK.",
            "itemListElement": items.map((item, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                    "@type": "Product",
                    "name": item.name,
                    "description": item.description,
                    "image": item.imageUrl,
                    "url": item.amazonLink, // The product URL is the affiliate link
                    "offers": {
                        "@type": "Offer",
                        "url": item.amazonLink,
                        "priceCurrency": "GBP",
                        "availability": "https://schema.org/InStock",
                        "seller": {
                            "@type": "Organization",
                            "name": "Amazon.co.uk"
                        }
                    }
                }
            }))
        };

        const webPageSchema = {
            "@type": "WebPage",
            "@id": canonicalUrl,
            "url": canonicalUrl,
            "name": "UK Essentials Checklist for Indians Moving to the UK (2026)",
            "description": "The 2026 checklist for Indians moving to the UK: what to pack and buy, from UK plug adapters and pressure cookers to winter wear. Your first shopping list.",
            "isPartOf": {
                "@type": "WebSite",
                "@id": "https://indianflatmates.co.uk/#website"
            }
        };

        return {
            "@context": "https://schema.org",
            // Products load from Firestore; an empty ItemList is invalid markup
            "@graph": items.length > 0 ? [webPageSchema, faqSchema, productSchema] : [webPageSchema, faqSchema]
        };
    };


    if (loading) {
        return <div className="text-center p-10">Loading essential items for your UK move...</div>;
    }

    return (
        <>
            <Helmet>
                <title>UK Essentials Checklist for Indians Moving to the UK (2026)</title>
                <meta
                    name="description"
                    content="The 2026 checklist for Indians moving to the UK: what to pack and buy, from UK plug adapters and pressure cookers to winter wear. Your first shopping list."
                />
                {/* Social previews (WhatsApp, Facebook, X) — otherwise the homepage values are used */}
                <meta property="og:title" content="UK Essentials Checklist for Indians Moving to the UK (2026)" />
                <meta property="og:description" content="The 2026 checklist for Indians moving to the UK: what to pack and buy, from UK plug adapters and pressure cookers to winter wear. Your first shopping list." />
                <meta name="twitter:title" content="UK Essentials Checklist for Indians Moving to the UK (2026)" />
                <meta name="twitter:description" content="The 2026 checklist for Indians moving to the UK: what to pack and buy, from UK plug adapters and pressure cookers to winter wear. Your first shopping list." />
                <link rel="canonical" href="https://indianflatmates.co.uk/uk-essentials-for-indians" />
                {/* ✨ MODIFIED: Injects the complete, combined schema. */}
                <script type="application/ld+json">
                    {JSON.stringify(generatePageSchema())}
                </script>
            </Helmet>

            <div className="container mx-auto p-4 md:p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md my-6">
                <div className="max-w-5xl mx-auto text-left">
                    <h1 className="text-3xl md:text-4xl font-extrabold text-indigo-700 dark:text-indigo-300 mb-4 text-center">
                        First Shopping List for Indians Arriving in the UK
                    </h1>
                    <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 text-center">
                        Moving to the UK is exciting! To make your first few weeks smoother, here’s a checklist of essential items many students, professionals, and families find they need soon after arriving.
                    </p>
                    
                    {/* ✨ NEW: "Jump to Category" section for improved UX and internal linking. */}
                    <div className="p-4 mb-8 bg-gray-50 dark:bg-gray-900/50 rounded-lg border dark:border-gray-700">
                        <h2 className="text-lg font-bold mb-3 text-gray-800 dark:text-gray-200">On this page</h2>
                        <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
                            {categories.map(category => (
                                <li key={`nav-${category.id}`}>
                                    <a href={`#${slugify(category.name)}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                                        {category.name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="text-center bg-yellow-50 dark:bg-yellow-900/50 border border-yellow-300 dark:border-yellow-700 p-3 rounded-md mb-10 text-sm text-yellow-800 dark:text-yellow-200">
                        <strong>Disclaimer:</strong> As an Amazon Associate, we earn from qualifying purchases at no extra cost to you. This helps support our work in providing helpful guides.
                    </div>

                   {categories.map(category => (
                        // ✨ MODIFIED: Added an ID to each section wrapper for anchor linking.
                        <section key={category.id} id={slugify(category.name)} className="mb-12 scroll-mt-20">
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6 pb-2 border-b-2 border-indigo-200 dark:border-indigo-800">
                                {category.name}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {items
                                    .filter(item => item.categoryId === category.id)
                                    .map(item => (
                                        <ItemCard key={item.id} item={item} />
                                    ))
                                }
                            </div>
                        </section>
                   ))}

                    {/* Static checklist: always in the prerendered HTML, unlike the Firestore product lists above */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6 pb-2 border-b-2 border-indigo-200 dark:border-indigo-800">Packing Checklist: What to Bring From India</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-gray-700 dark:text-gray-300">
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Pack in your hand luggage</h3>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Passport and your visa or eVisa details</li>
                                    <li>University CAS or job offer letter</li>
                                    <li>Original degree certificates and transcripts</li>
                                    <li>TB test certificate (if you needed one for your visa)</li>
                                    <li>Prescription medicines with a copy of the prescription</li>
                                    <li>Some pounds in cash or a forex card for your first days</li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Worth bringing from home</h3>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Traditional outfits for Diwali, weddings and festivals</li>
                                    <li>Favourite masalas and spices in sealed, labelled packets</li>
                                    <li>A small pressure cooker if you cook Indian food every day</li>
                                    <li>Passport-size photos for university and other forms</li>
                                    <li>Everyday medicines you rely on, like pain relief and antacids</li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Easier to buy in the UK</h3>
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Duvet, pillows and bedding in UK sizes</li>
                                    <li>A proper winter coat suited to UK weather</li>
                                    <li>UK plug adapters and extension leads</li>
                                    <li>A UK SIM card</li>
                                    <li>Kitchen basics and toiletries from supermarkets and discount stores</li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold mb-2">What you can't bring</h3>
                                <p>You cannot bring meat, milk or dairy products — including ghee, paneer and milk sweets — from India into the UK. Fresh fruit, vegetables, plants and seeds are also restricted. Check the rules on <a href="https://www.gov.uk/bringing-food-into-great-britain" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">GOV.UK</a> before you pack.</p>
                            </div>
                        </div>
                    </section>

                    <FaqSection />

                </div>
            </div>
        </>
    );
}

export default UkEssentials;